import { useMutation } from '@tanstack/react-query'
import { FormattedMessage, useIntl } from 'react-intl'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'

import { BusinessRoute } from '@/constants/paths'
import { queryKeys } from '@/constants/queryKeys'
import { useErrorToast } from '@/hooks/useErrorToast'
import { queryClient } from '@/lib/queryClient'
import { getFullName } from '@/lib/typography'
import { DeleteDialog } from '@/shared/components'

import { deleteContractor } from '../../api'
import { ContractorDetails } from '../../types'

type Props = {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  contractor?: ContractorDetails
}

export const DeleteContractorDialog = ({
  contractor,
  isOpen,
  onOpenChange,
}: Props) => {
  const notifyError = useErrorToast()
  const navigate = useNavigate()
  const intl = useIntl()

  const { mutateAsync, isPending, isSuccess } = useMutation({
    mutationFn: deleteContractor,
  })

  const handleDelete = async () => {
    if (!contractor?.id) {
      toast.error('Contractor ID not found')

      return
    }

    try {
      await mutateAsync(contractor.id)

      await queryClient.refetchQueries({
        queryKey: [queryKeys.getContractors],
      })

      onOpenChange(false)

      toast.success(
        intl.formatMessage({
          id: 'contractor.delete.success',
          defaultMessage: 'Contractor has been deleted',
        }),
      )

      navigate(BusinessRoute.ContractorsAll)
    } catch (error) {
      notifyError(error)
    }
  }

  return (
    <DeleteDialog
      title={
        <FormattedMessage
          defaultMessage="Delete contractor?"
          id="contractor.delete.dialog.title"
        />
      }
      description={
        <FormattedMessage
          defaultMessage="Are you sure you want to delete {fullName}?"
          id="contractor.delete.dialog.deleteContractor"
          values={{ fullName: getFullName(contractor?.personalDetails) }}
        />
      }
      descriptionAdditional={
        <FormattedMessage
          defaultMessage="The contractor will be deleted permanently"
          id="contractor.delete.dialog.contractorDeleted"
        />
      }
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      isPending={isPending || isSuccess}
      onDelete={handleDelete}
    />
  )
}
